import { watch, onUnmounted, nextTick } from 'vue'

/**
 * Houdt de toetsenbord-focus binnen een geopende modal/dialoog (Tab-cyclus),
 * zet de focus bij openen op het eerste element en geeft 'm bij sluiten terug
 * aan het element dat de modal opende. SSR-veilig.
 *
 * @param {import('vue').Ref<boolean>} isOpen        Reactieve open-status.
 * @param {import('vue').Ref<HTMLElement|null>} containerRef  Ref naar het modal-element.
 */
export function useFocusTrap(isOpen, containerRef) {
  if (typeof window === 'undefined') return

  let prevActive = null
  const SELECTOR = [
    'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
    'input:not([disabled])', 'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',')

  const focusables = () => {
    const el = containerRef.value
    if (!el) return []
    return Array.from(el.querySelectorAll(SELECTOR))
      .filter(n => n.offsetParent !== null || n === document.activeElement)
  }

  const onKey = (e) => {
    if (e.key !== 'Tab') return
    const items = focusables()
    if (!items.length) return
    const first = items[0]
    const last = items[items.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus()
    } else if (!items.includes(document.activeElement)) {
      // Focus buiten de modal beland → terughalen
      e.preventDefault(); first.focus()
    }
  }

  watch(isOpen, (open) => {
    if (open) {
      prevActive = document.activeElement
      document.addEventListener('keydown', onKey, true)
      nextTick(() => { const items = focusables(); (items[0] || containerRef.value)?.focus?.() })
    } else {
      document.removeEventListener('keydown', onKey, true)
      if (prevActive && typeof prevActive.focus === 'function') prevActive.focus()
      prevActive = null
    }
  }, { immediate: true })

  onUnmounted(() => document.removeEventListener('keydown', onKey, true))
}
